import { Component, inject, OnInit, signal, DestroyRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AccessPolicyService, AccessPolicy, AccessPolicyRequest, Scope, CreateScopeRequest } from '../../../service/access-policy/access-policy.service';
import { TextareaModule } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

@Component({
  selector: 'app-access-policies',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    MultiSelectModule,
    TagModule,
    ChipModule,
    PageLayoutComponent,
    TextareaModule,
    ProgressSpinnerModule,
    StateContainerComponent
  ],
  templateUrl: './access-policies.component.html',
  providers: [MessageService, ConfirmationService]
})
export class AccessPoliciesComponent implements OnInit {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private accessPolicyService = inject(AccessPolicyService);
  private destroyRef = inject(DestroyRef);

  // Data
  policies = signal<AccessPolicy[]>([]);
  availableScopes = signal<Scope[]>([]);
  scopeOptions = signal<{ label: string; value: string }[]>([]);

  // Table / server-pagination state
  loading = signal(false);
  saving = signal(false);
  totalRecords = signal(0);
  pageSize = 20;
  currentPage = 0;
  sortField = '';
  sortOrder = 0;
  
  // Flag to prevent duplicate API calls
  private isInitialLoad = true;

  // Filters
  searchTerm = '';
  targetFilter: string | null = null;
  statusFilter: string | null = null;
  private searchSubject = new Subject<void>();

  // Dialog state
  showDialog = false;
  dialogMode: 'create' | 'edit' = 'create';
  editingPolicy: AccessPolicy | null = null;
  viewMode = false;

  // Scope creation dialog
  showScopeDialog = false;
  isCreatingScope = false;
  scopeForm!: FormGroup;

  policyForm!: FormGroup;

  // Error state
  error = signal<any>(null);

  targetFilterOptions = [
    { label: 'All Targets', value: null },
    { label: 'User Groups', value: 'Identity Group' },
    { label: 'API Groups', value: 'Api Group' }
  ];

  targetTypeOptions = [
    { label: 'User Group', value: 'Identity Group' },
    { label: 'API Group', value: 'Api Group' }
  ];

  statusFilterOptions = [
    { label: 'All Statuses', value: null },
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
    { label: 'Draft', value: 'Draft' }
  ];

  statusSelectOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
    { label: 'Draft', value: 'Draft' }
  ];

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.policies().length === 0) return 'empty';
    return 'content';
  });

  readonly showClearFilters = computed(() => {
    return !!(this.searchTerm || this.targetFilter || this.statusFilter);
  });

  readonly emptyStateMessage = computed(() => {
    if (this.searchTerm || this.targetFilter || this.statusFilter) {
      return 'No policies match your current search or filter criteria. Try adjusting your filters.';
    }
    return 'Create your first access policy to control access to users, groups, APIs, and API groups.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load access policies. Please try again.';
  });

  ngOnInit() {
    this.initForm();
    this.initScopeForm();
    this.loadPolicies();
    this.loadScopes();

    this.searchSubject
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.currentPage = 0;
        this.loadPolicies();
      });
  }

  initForm() {
    this.policyForm = this.fb.group({
      name: ['', Validators.required],
      targetType: ['Identity Group', Validators.required],
      scopes: [[]],
      status: ['Active'],
      description: ['']
    });
  }

  initScopeForm() {
    this.scopeForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._-]+$/)]],
      description: ['']
    });
  }

  loadPolicies() {
    if (this.loading()) {
      return;
    }
    
    this.loading.set(true);
    this.error.set(null);
    const sort = this.sortField && this.sortOrder !== 0
      ? `${this.sortField},${this.sortOrder === 1 ? 'asc' : 'desc'}`
      : undefined;

    this.accessPolicyService.getAccessPolicies(
      this.currentPage,
      this.pageSize,
      this.searchTerm,
      this.targetFilter ?? undefined,
      this.statusFilter ?? undefined,
      sort
    ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.policies.set(response.content);
          this.totalRecords.set(response.page.totalElements);
          this.loading.set(false);
          this.isInitialLoad = false;
        },
        error: (error) => {
          this.error.set(error);
          this.loading.set(false);
          this.isInitialLoad = false;
          this.showError('Failed to load access policies');
        }
      });
  }

  // Retry method for error state
  retryLoad() {
    this.loadPolicies();
  }

  clearFilters() {
    this.searchTerm = '';
    this.targetFilter = null;
    this.statusFilter = null;
    this.currentPage = 0;
    this.loadPolicies();
  }

  loadScopes() {
    this.accessPolicyService.getScopes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (scopes) => {
          this.availableScopes.set(scopes);
          this.updateScopeOptions(scopes);
        },
        error: (error) => {
          console.error('Failed to load scopes:', error);
          this.updateScopeOptions([]);
        }
      });
  }

  private updateScopeOptions(scopes: Scope[]) {
    let options: { label: string; value: string }[] = [];
    
    if (scopes && scopes.length > 0) {
      options = scopes.map(scope => ({
        label: scope.name,
        value: scope.name
      }));
    }
    
    this.scopeOptions.set(options);
  }

  openCreateScopeDialog(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.scopeForm.reset({
      name: '',
      description: ''
    });
    this.showScopeDialog = true;
  }

  createScope() {
    if (this.scopeForm.invalid) {
      this.scopeForm.markAllAsTouched();
      return;
    }

    const formValue = this.scopeForm.value;
    const payload: CreateScopeRequest = {
      name: formValue.name.trim(),
      description: formValue.description || ''
    };

    const existingScope = this.scopeOptions().find(opt => opt.value === payload.name);
    if (existingScope) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Duplicate Scope',
        detail: `Scope "${payload.name}" already exists`
      });
      return;
    }

    this.isCreatingScope = true;

    this.accessPolicyService.createScope(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (newScope) => {
          this.isCreatingScope = false;
          this.showScopeDialog = false;
          
          const currentScopes = this.availableScopes();
          this.availableScopes.set([...currentScopes, newScope]);
          const updatedOptions = this.scopeOptions();
          updatedOptions.push({ label: newScope.name, value: newScope.name });
          this.scopeOptions.set(updatedOptions);
          const currentScopesValue = this.policyForm.get('scopes')?.value || [];
          this.policyForm.patchValue({
            scopes: [...currentScopesValue, newScope.name]
          });

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Scope "${newScope.name}" created successfully`
          });
        },
        error: (error) => {
          this.isCreatingScope = false;
          console.error('Failed to create scope:', error);
          this.showError('Failed to create scope. Please try again.');
        }
      });
  }

  onSearchChange() {
    this.searchSubject.next();
  }

  onFilterChange() {
    this.currentPage = 0;
    this.loadPolicies();
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    if (this.isInitialLoad) {
      return;
    }
    
    this.pageSize = event.rows || this.pageSize;
    this.currentPage = Math.floor((event.first ?? 0) / this.pageSize);
    this.sortField = (event.sortField as string) || '';
    this.sortOrder = event.sortOrder ?? 0;
    this.loadPolicies();
  }

  viewPolicyDetails(policy: AccessPolicy) {
    this.viewMode = true;
    this.dialogMode = 'edit';
    this.editingPolicy = policy;
    
    this.loading.set(true);
    
    this.accessPolicyService.getAccessPolicyById(policy.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fullPolicy) => {
          this.policies.update(policies => 
            policies.map(p => p.id === fullPolicy.id ? fullPolicy : p)
          );
          
          this.policyForm.patchValue({
            name: fullPolicy.name,
            targetType: fullPolicy.targetType,
            scopes: fullPolicy.scopes || [],
            status: fullPolicy.status,
            description: fullPolicy.description || ''
          });
          
          this.policyForm.disable();
          this.showDialog = true;
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.showError('Failed to load policy details');
          
          // Fallback
          this.policyForm.patchValue({
            name: policy.name,
            targetType: policy.targetType,
            scopes: policy.scopes || [],
            status: policy.status,
            description: policy.description || ''
          });
          this.policyForm.disable();
          this.showDialog = true;
        }
      });
  }

  openCreatePolicyDialog() {
    this.viewMode = false;
    this.dialogMode = 'create';
    this.editingPolicy = null;
    this.policyForm.reset({
      name: '',
      targetType: 'Identity Group',
      scopes: [],
      status: 'Active',
      description: ''
    });
    this.policyForm.enable();
    this.showDialog = true;
  }

  editPolicy(policy: AccessPolicy) {
    this.viewMode = false;
    this.dialogMode = 'edit';
    this.editingPolicy = policy;
    
    this.loading.set(true);
    
    this.accessPolicyService.getAccessPolicyById(policy.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fullPolicy) => {
          this.policies.update(policies => 
            policies.map(p => p.id === fullPolicy.id ? fullPolicy : p)
          );
          
          this.policyForm.patchValue({
            name: fullPolicy.name,
            targetType: fullPolicy.targetType,
            scopes: fullPolicy.scopes || [],
            status: fullPolicy.status,
            description: fullPolicy.description || ''
          });
          
          this.policyForm.enable();
          this.showDialog = true;
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.showError('Failed to load policy details');
          
          this.policyForm.patchValue({
            name: policy.name,
            targetType: policy.targetType,
            scopes: policy.scopes || [],
            status: policy.status,
            description: policy.description || ''
          });
          this.policyForm.enable();
          this.showDialog = true;
        }
      });
  }

  deletePolicy(policy: AccessPolicy) {
    this.confirmationService.confirm({
      header: 'Confirm Delete',
      message: `Are you sure you want to delete policy "${policy.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.accessPolicyService.deleteAccessPolicy(policy.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Policy "${policy.name}" deleted successfully`
              });
              this.loadPolicies();
            },
            error: () => this.showError(`Failed to delete policy "${policy.name}"`)
          });
      }
    });
  }

  savePolicy() {
    if (this.policyForm.invalid) {
      this.policyForm.markAllAsTouched();
      return;
    }

    const formValue = this.policyForm.value;
    const payload: AccessPolicyRequest = {
      name: formValue.name,
      description: formValue.description || '',
      targetType: formValue.targetType,
      scopes: formValue.scopes || [],
      status: formValue.status
    };

    this.saving.set(true);

    const request$ = this.dialogMode === 'create'
      ? this.accessPolicyService.createAccessPolicy(payload)
      : this.accessPolicyService.updateAccessPolicy(this.editingPolicy!.id, payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Policy "${payload.name}" ${this.dialogMode === 'create' ? 'created' : 'updated'} successfully`
        });
        this.closeDialog();
        this.loadPolicies();
      },
      error: () => {
        this.saving.set(false);
        this.showError(`Failed to ${this.dialogMode === 'create' ? 'create' : 'update'} policy "${payload.name}"`);
      }
    });
  }

  closeDialog() {
    this.showDialog = false;
    this.editingPolicy = null;
    this.viewMode = false;
    this.policyForm.enable();
  }

  private showError(detail: string) {
    this.messageService.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail 
    });
  }
}